import { useCallback, useEffect, useState, type CSSProperties } from 'react';
import Cropper, { type Area, type MediaSize } from 'react-easy-crop';
import 'react-easy-crop/react-easy-crop.css';
import { getWideCropInitialZoom } from '@/features/profile/edit/crop/cropInitialZoom';
import { getCroppedImageBlob } from '@/features/profile/edit/crop/cropImage';
import {
  getProfileImageCropPreset,
  type ProfileImageCropTarget,
} from '@/features/profile/edit/crop/profileImageCropConfig';
import { useProfileImageCropStore } from '@/features/profile/edit/crop/profileImageCropStore';
import Button from '@/shared/ui/Button';
import Modal from '@/shared/ui/Modal';
import ModalHeader from '@/shared/ui/ModalHeader';
import { textStyles } from '@/shared/tokens/typography';
import { cn } from '@/shared/lib/cn';

interface ProfileImageCropModalProps {
  target: ProfileImageCropTarget;
  imageSrc: string;
  onClose: () => void;
}

const CROP_AREA_STYLE: CSSProperties = {
  border: '2px solid rgba(255, 255, 255, 0.95)',
  boxShadow: '0 0 0 100vmax rgba(0, 0, 0, 0.45)',
};

export default function ProfileImageCropModal({
  target,
  imageSrc,
  onClose,
}: ProfileImageCropModalProps) {
  const preset = getProfileImageCropPreset(target);
  const applyCroppedBlob = useProfileImageCropStore((state) => state.applyCroppedBlob);
  const isBanner = target === 'banner';

  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);
  const [isApplying, setIsApplying] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setCroppedAreaPixels(null);
    setError(null);
    setIsApplying(false);
  }, [imageSrc, target]);

  const onCropComplete = useCallback((_area: Area, pixels: Area) => {
    setCroppedAreaPixels(pixels);
  }, []);

  const onMediaLoaded = useCallback(
    (media: MediaSize) => {
      if (!isBanner) {
        return;
      }
      setZoom(getWideCropInitialZoom(media, preset.aspect));
    },
    [isBanner, preset.aspect],
  );

  const handleApply = async () => {
    if (!croppedAreaPixels) {
      return;
    }
    setIsApplying(true);
    setError(null);
    try {
      const blob = await getCroppedImageBlob(
        imageSrc,
        croppedAreaPixels,
        preset.outputWidth,
        preset.outputHeight,
      );
      applyCroppedBlob(blob);
    } catch {
      setError('Could not process this image. Try another file.');
      setIsApplying(false);
    }
  };

  const cropHint = isBanner
    ? 'Move and zoom so the wide strip matches your profile background. The preview above is how it will appear on your profile.'
    : 'Drag to reposition · Use the slider to zoom · Saved as a square profile photo';

  return (
    <Modal
      onClose={onClose}
      className={cn('flex w-full flex-col overflow-hidden p-0', preset.modalClassName)}
      ariaLabel={preset.title}
      zIndexClassName="z-[110]"
    >
      <ModalHeader title={preset.title} onClose={onClose} />
      <div className="px-4 pb-2 sm:px-6">
        <p className={textStyles.caption}>{cropHint}</p>
      </div>
      <div
        className={cn(
          'relative mx-4 overflow-hidden rounded-xl bg-gray-950 sm:mx-6',
          preset.cropViewportClassName,
        )}
      >
        <Cropper
          image={imageSrc}
          crop={crop}
          zoom={zoom}
          aspect={preset.aspect}
          cropShape={preset.cropShape}
          objectFit={preset.objectFit}
          minZoom={preset.minZoom}
          maxZoom={preset.maxZoom}
          showGrid={isBanner}
          zoomWithScroll
          onCropChange={setCrop}
          onZoomChange={setZoom}
          onCropComplete={onCropComplete}
          onMediaLoaded={onMediaLoaded}
          style={{ cropAreaStyle: CROP_AREA_STYLE }}
          classes={{
            containerClassName: '!absolute inset-0',
          }}
        />
      </div>
      <div className="space-y-2 px-4 py-4 sm:px-6">
        <div className="flex items-center justify-between gap-3">
          <label htmlFor="crop-zoom" className={textStyles.label}>
            Zoom
          </label>
          <span className={textStyles.caption}>{Math.round(zoom * 100)}%</span>
        </div>
        <input
          id="crop-zoom"
          type="range"
          min={preset.minZoom}
          max={preset.maxZoom}
          step={0.02}
          value={zoom}
          onChange={(event) => setZoom(Number(event.target.value))}
          className="h-2 w-full cursor-pointer accent-[#00A7C8]"
        />
        {error ? <p className="text-sm text-red-600">{error}</p> : null}
        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="ghost" onClick={onClose} disabled={isApplying}>
            Cancel
          </Button>
          <Button
            type="button"
            variant="primary"
            onClick={() => void handleApply()}
            disabled={isApplying || !croppedAreaPixels}
          >
            {isApplying ? 'Applying…' : 'Use image'}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
